import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  DesignServices as DesignIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  PhoneAndroid as MobileIcon,
  ShoppingCart as EcommerceIcon,
  Accessibility as AccessibilityIcon,
  TrendingUp as TrendingIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

const CanvaExport = ({ business, websiteAnalysis }) => {
  const [selectedSections, setSelectedSections] = useState(['summary', 'performance', 'security', 'seo']);
  const [copiedText, setCopiedText] = useState('');

  if (!business || !websiteAnalysis) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="text.secondary">
          Analysez d'abord un site web pour générer l'export Canva
        </Typography>
      </Box>
    );
  }

  const sections = {
    summary: {
      title: '📊 Résumé Exécutif',
      icon: <TrendingIcon />,
      content: generateSummarySection(business, websiteAnalysis)
    },
    performance: {
      title: '🚀 Performance',
      icon: <SpeedIcon />,
      content: generatePerformanceSection(websiteAnalysis.performance)
    },
    security: {
      title: '🔒 Sécurité',
      icon: <SecurityIcon />,
      content: generateSecuritySection(websiteAnalysis.security)
    },
    seo: {
      title: '📈 SEO & Visibilité',
      icon: <VisibilityIcon />,
      content: generateSEOSection(websiteAnalysis.seo)
    },
    mobile: {
      title: '📱 Mobile',
      icon: <MobileIcon />,
      content: generateMobileSection(websiteAnalysis.mobile)
    },
    ecommerce: {
      title: '🛒 E-commerce',
      icon: <EcommerceIcon />,
      content: generateEcommerceSection(websiteAnalysis.ecommerce)
    },
    accessibility: {
      title: '♿ Accessibilité',
      icon: <AccessibilityIcon />,
      content: generateAccessibilitySection(websiteAnalysis.accessibility)
    }
  };

  function generateSummarySection(business, analysis) {
    const criticalIssues = analysis.criticalIssues || 0;
    const overallScore = analysis.overallScore || 0;
    
    return {
      title: `AUDIT SITE WEB - ${business.name}`,
      subtitle: `Analyse technique complète - ${new Date().toLocaleDateString()}`,
      metrics: [
        { label: 'Score Global', value: `${overallScore}/100`, color: overallScore < 50 ? 'error' : overallScore < 70 ? 'warning' : 'success' },
        { label: 'Problèmes Critiques', value: criticalIssues.toString(), color: criticalIssues > 0 ? 'error' : 'success' },
        { label: 'Gain Potentiel', value: '+150% à +300%', color: 'success' },
        { label: 'ROI Attendu', value: '3-6 mois', color: 'info' }
      ],
      highlights: [
        `Site web analysé : ${business.website}`,
        `Score global : ${overallScore}/100`,
        `${criticalIssues} problème${criticalIssues > 1 ? 's' : ''} critique${criticalIssues > 1 ? 's' : ''} identifié${criticalIssues > 1 ? 's' : ''}`,
        `Potentiel d'amélioration : +150% à +300% de conversions`,
        `Investissement recommandé : ${analysis.estimatedBudget}€`
      ]
    };
  }

  function generatePerformanceSection(performance) {
    return {
      title: 'ANALYSE PERFORMANCE',
      score: performance.performanceScore,
      metrics: [
        { label: 'Temps de chargement', value: `${performance.loadTime}s`, status: performance.loadTime > 3 ? 'error' : 'success' },
        { label: 'Taille de page', value: `${Math.round(performance.pageSize / 1024)}KB`, status: performance.pageSize > 2000 ? 'error' : 'success' },
        { label: 'Requêtes HTTP', value: performance.requests.toString(), status: performance.requests > 80 ? 'error' : 'success' },
        { label: 'First Contentful Paint', value: `${performance.metrics.firstContentfulPaint}s`, status: performance.metrics.firstContentfulPaint > 1.8 ? 'error' : 'success' }
      ],
      issues: performance.issues,
      optimizations: performance.optimizations,
      impact: 'Impact sur les conversions : -7% par seconde de délai'
    };
  }

  function generateSecuritySection(security) {
    return {
      title: 'ANALYSE SÉCURITÉ',
      score: security.securityScore,
      checks: [
        { label: 'HTTPS/SSL', status: security.hasHTTPS ? 'success' : 'error', value: security.hasHTTPS ? '✅ Actif' : '❌ Manquant' },
        { label: 'Headers de sécurité', status: security.hasSecurityHeaders ? 'success' : 'error', value: security.hasSecurityHeaders ? '✅ Configurés' : '❌ Manquants' },
        { label: 'Content Security Policy', status: security.hasCSP ? 'success' : 'error', value: security.hasCSP ? '✅ Actif' : '❌ Manquant' },
        { label: 'Cookies sécurisés', status: security.hasSecureCookies ? 'success' : 'error', value: security.hasSecureCookies ? '✅ Configurés' : '❌ Non sécurisés' }
      ],
      issues: security.issues,
      vulnerabilities: security.vulnerabilities,
      impact: 'Risque de piratage et perte de confiance clients'
    };
  }

  function generateSEOSection(seo) {
    return {
      title: 'ANALYSE SEO & VISIBILITÉ',
      score: seo.seoScore,
      checks: [
        { label: 'Meta descriptions', status: seo.hasMetaDescription ? 'success' : 'error', value: seo.hasMetaDescription ? '✅ Présentes' : '❌ Manquantes' },
        { label: 'Balises title', status: seo.hasTitle ? 'success' : 'error', value: seo.hasTitle ? '✅ Présentes' : '❌ Manquantes' },
        { label: 'Sitemap XML', status: seo.hasSitemap ? 'success' : 'error', value: seo.hasSitemap ? '✅ Présent' : '❌ Manquant' },
        { label: 'Balises alt', status: seo.hasAltTags ? 'success' : 'error', value: seo.hasAltTags ? '✅ Présentes' : '❌ Manquantes' }
      ],
      metrics: [
        { label: 'Backlinks', value: seo.backlinks.toString() },
        { label: 'Domain Authority', value: seo.domainAuthority.toString() },
        { label: 'Mots-clés identifiés', value: seo.keywords.length.toString() }
      ],
      issues: seo.issues,
      impact: 'Perte de 90% du trafic organique potentiel'
    };
  }

  function generateMobileSection(mobile) {
    return {
      title: 'ANALYSE MOBILE',
      score: mobile.mobileScore,
      checks: [
        { label: 'Design responsive', status: mobile.isResponsive ? 'success' : 'error', value: mobile.isResponsive ? '✅ Adapté' : '❌ Non adapté' },
        { label: 'Boutons tactiles', status: mobile.hasTouchTargets ? 'success' : 'error', value: mobile.hasTouchTargets ? '✅ Optimisés' : '❌ Trop petits' },
        { label: 'Menu mobile', status: mobile.hasMobileMenu ? 'success' : 'error', value: mobile.hasMobileMenu ? '✅ Présent' : '❌ Manquant' },
        { label: 'Meta viewport', status: mobile.hasViewport ? 'success' : 'error', value: mobile.hasViewport ? '✅ Configuré' : '❌ Manquant' }
      ],
      metrics: [
        { label: 'Trafic mobile', value: `${mobile.mobileTraffic}%` },
        { label: 'Conversion mobile', value: `${mobile.mobileConversion}%` }
      ],
      issues: mobile.issues,
      impact: 'Perte de 65% des visiteurs mobiles'
    };
  }

  function generateEcommerceSection(ecommerce) {
    return {
      title: 'ANALYSE E-COMMERCE',
      score: ecommerce.ecommerceScore,
      checks: [
        { label: 'Panier d\'achat', status: ecommerce.hasShoppingCart ? 'success' : 'error', value: ecommerce.hasShoppingCart ? '✅ Présent' : '❌ Manquant' },
        { label: 'Moyens de paiement', status: ecommerce.hasPaymentGateway ? 'success' : 'error', value: ecommerce.hasPaymentGateway ? '✅ Configurés' : '❌ Manquants' },
        { label: 'Catalogue produits', status: ecommerce.hasProductCatalog ? 'success' : 'error', value: ecommerce.hasProductCatalog ? '✅ Complet' : '❌ Incomplet' },
        { label: 'Système d\'avis', status: ecommerce.hasReviews ? 'success' : 'error', value: ecommerce.hasReviews ? '✅ Présent' : '❌ Manquant' }
      ],
      metrics: [
        { label: 'Taux de conversion', value: `${ecommerce.conversionRate}%` },
        { label: 'Abandon panier', value: `${ecommerce.cartAbandonment}%` },
        { label: 'Panier moyen', value: `${ecommerce.averageOrderValue}€` }
      ],
      issues: ecommerce.issues,
      impact: 'Perte de 60% des ventes potentielles'
    };
  }

  function generateAccessibilitySection(accessibility) {
    return {
      title: 'ANALYSE ACCESSIBILITÉ',
      score: accessibility.accessibilityScore,
      checks: [
        { label: 'Alt text images', status: accessibility.hasAltText ? 'success' : 'error', value: accessibility.hasAltText ? '✅ Présents' : '❌ Manquants' },
        { label: 'Navigation clavier', status: accessibility.hasKeyboardNav ? 'success' : 'error', value: accessibility.hasKeyboardNav ? '✅ Possible' : '❌ Impossible' },
        { label: 'Contraste couleurs', status: accessibility.hasColorContrast ? 'success' : 'error', value: accessibility.hasColorContrast ? '✅ Suffisant' : '❌ Insuffisant' },
        { label: 'HTML sémantique', status: accessibility.hasSemanticHTML ? 'success' : 'error', value: accessibility.hasSemanticHTML ? '✅ Correct' : '❌ Incorrect' }
      ],
      metrics: [
        { label: 'Conformité WCAG', value: `Niveau ${accessibility.wcagCompliance}` },
        { label: 'Lecteur d\'écran', value: accessibility.screenReaderCompatible ? '✅ Compatible' : '❌ Incompatible' }
      ],
      issues: accessibility.issues,
      impact: 'Exclusion de 15% de la population'
    };
  }

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const downloadCanvaData = () => {
    const canvaData = {
      business: business.name,
      website: business.website,
      date: new Date().toLocaleDateString(),
      sections: {}
    };

    selectedSections.forEach(sectionKey => {
      if (sections[sectionKey]) {
        canvaData.sections[sectionKey] = sections[sectionKey].content;
      }
    });

    const content = `DONNÉES CANVA - ${business.name}

${canvaData.sections.summary?.title || 'AUDIT SITE WEB'}
${canvaData.sections.summary?.subtitle || ''}

MÉTRIQUES PRINCIPALES:
${canvaData.sections.summary?.metrics?.map(m => `• ${m.label}: ${m.value}`).join('\n') || ''}

POINTS CLÉS:
${canvaData.sections.summary?.highlights?.map(h => `• ${h}`).join('\n') || ''}

${selectedSections.map(sectionKey => {
  const section = sections[sectionKey];
  if (!section || !canvaData.sections[sectionKey]) return '';
  
  const content = canvaData.sections[sectionKey];
  return `
${content.title}
Score: ${content.score}/100

${content.checks ? `VÉRIFICATIONS:
${content.checks.map(c => `• ${c.label}: ${c.value}`).join('\n')}` : ''}

${content.metrics ? `MÉTRIQUES:
${content.metrics.map(m => `• ${m.label}: ${m.value}`).join('\n')}` : ''}

${content.issues ? `PROBLÈMES IDENTIFIÉS:
${content.issues.map(i => `• ${i}`).join('\n')}` : ''}

${content.optimizations ? `OPTIMISATIONS RECOMMANDÉES:
${content.optimizations.map(o => `• ${o}`).join('\n')}` : ''}

Impact: ${content.impact || ''}
`;
}).join('\n')}

RECOMMANDATIONS GÉNÉRALES:
• Optimisation des performances pour améliorer l'expérience utilisateur
• Sécurisation du site pour protéger les données clients
• Optimisation SEO pour augmenter la visibilité
• Adaptation mobile pour capturer tous les visiteurs
• Amélioration de l'accessibilité pour inclure tous les utilisateurs

BUDGET ESTIMÉ: ${websiteAnalysis.estimatedBudget}€
GAIN POTENTIEL: +150% à +300% de conversions
ROI ATTENDU: 3-6 mois
`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `canva-data-${business.name.replace(/\s+/g, '-').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DesignIcon color="primary" />
        Export Optimisé pour Canva
      </Typography>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Générez des données structurées pour vos templates Canva avec des métriques détaillées et des sections organisées.
      </Typography>

      {/* Sélection des sections */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sections à inclure dans l'export
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(sections).map(([key, section]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Chip
                  icon={section.icon}
                  label={section.title}
                  onClick={() => {
                    if (selectedSections.includes(key)) {
                      setSelectedSections(selectedSections.filter(s => s !== key));
                    } else {
                      setSelectedSections([...selectedSections, key]);
                    }
                  }}
                  color={selectedSections.includes(key) ? 'primary' : 'default'}
                  variant={selectedSections.includes(key) ? 'filled' : 'outlined'}
                  sx={{ width: '100%', justifyContent: 'flex-start' }}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Aperçu des données */}
      <Grid container spacing={3}>
        {selectedSections.map(sectionKey => {
          const section = sections[sectionKey];
          const content = section.content;
          
          return (
            <Grid item xs={12} md={6} key={sectionKey}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {section.icon}
                    {content.title}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label={`Score: ${content.score}/100`}
                      color={content.score < 50 ? 'error' : content.score < 70 ? 'warning' : 'success'}
                      sx={{ mb: 1 }}
                    />
                  </Box>

                  {content.checks && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Vérifications:
                      </Typography>
                      <List dense>
                        {content.checks.map((check, index) => (
                          <ListItem key={index} sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              {check.status === 'success' ? <CheckIcon color="success" /> : <ErrorIcon color="error" />}
                            </ListItemIcon>
                            <ListItemText 
                              primary={`${check.label}: ${check.value}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {content.metrics && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Métriques:
                      </Typography>
                      <Grid container spacing={1}>
                        {content.metrics.map((metric, index) => (
                          <Grid item xs={6} key={index}>
                            <Chip 
                              label={`${metric.label}: ${metric.value}`}
                              size="small"
                              variant="outlined"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {content.issues && content.issues.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom color="error">
                        Problèmes identifiés:
                      </Typography>
                      <List dense>
                        {content.issues.slice(0, 3).map((issue, index) => (
                          <ListItem key={index} sx={{ py: 0 }}>
                            <ListItemIcon sx={{ minWidth: 30 }}>
                              <WarningIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={issue}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {content.impact && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {content.impact}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={downloadCanvaData}
          size="large"
        >
          Télécharger Données Canva
        </Button>
        <Button
          variant="outlined"
          startIcon={<CopyIcon />}
          onClick={() => copyToClipboard(
            `AUDIT ${business.name} - Score: ${websiteAnalysis.overallScore}/100 - ${websiteAnalysis.criticalIssues} problèmes critiques`,
            'resume'
          )}
          size="large"
        >
          Copier Résumé
        </Button>
      </Box>

      {copiedText && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Résumé copié !
        </Alert>
      )}

      {/* Conseils d'utilisation */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            💡 Conseils pour vos Templates Canva
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Utilisez les scores colorés" 
                secondary="Rouge (0-50), Orange (51-70), Vert (71-100)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Mettez en avant les problèmes critiques" 
                secondary="Priorisez les éléments avec le plus d'impact business"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Incluez les métriques de performance" 
                secondary="Temps de chargement, taille de page, requêtes HTTP"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckIcon color="success" />
              </ListItemIcon>
              <ListItemText 
                primary="Ajoutez les gains potentiels" 
                secondary="+150% à +300% de conversions, ROI en 3-6 mois"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CanvaExport;